import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { MatierePremiere } from '@/types';
import { useCreateMatiere, useUpdateMatiere } from '@/hooks/useBetonCatalog';
import { extractError } from '@/api/client';

interface Props {
  open:     boolean;
  onClose:  () => void;
  matiere?: MatierePremiere;
}

interface FormState {
  code:            string;
  nom:             string;
  description:     string;
  unite:           string;
  prixUnitaireFcfa: string;
  fournisseurNom:  string;
  stockActuel:     string;
  seuilAlerte:     string;
  actif:           boolean;
}

const empty: FormState = {
  code: '', nom: '', description: '', unite: 'kg',
  prixUnitaireFcfa: '', fournisseurNom: '', stockActuel: '0', seuilAlerte: '0', actif: true,
};

const toForm = (m: MatierePremiere): FormState => ({
  code:             m.code,
  nom:              m.nom,
  description:      m.description ?? '',
  unite:            m.unite,
  prixUnitaireFcfa: String(Number(BigInt(m.prixUnitaireCents)) / 100),
  fournisseurNom:   m.fournisseurNom ?? '',
  stockActuel:      m.stockActuel,
  seuilAlerte:      m.seuilAlerte,
  actif:            m.actif,
});

export const MatiereFormDialog = ({ open, onClose, matiere }: Props) => {
  const [form, setForm] = useState<FormState>(empty);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateMatiere();
  const update = useUpdateMatiere();

  useEffect(() => {
    if (open) {
      setForm(matiere ? toForm(matiere) : empty);
      setError(null);
    }
  }, [open, matiere]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const payload = {
      code:              form.code.trim(),
      nom:               form.nom.trim(),
      description:       form.description.trim() || undefined,
      unite:             form.unite.trim(),
      prixUnitaireCents: Math.round(parseFloat(form.prixUnitaireFcfa || '0') * 100),
      fournisseurNom:    form.fournisseurNom.trim() || undefined,
      stockActuel:       parseFloat(form.stockActuel || '0'),
      seuilAlerte:       parseFloat(form.seuilAlerte || '0'),
      actif:             form.actif,
    };
    try {
      if (matiere) await update.mutateAsync({ id: matiere.id, payload });
      else         await create.mutateAsync(payload);
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  const submitting = create.isPending || update.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={matiere ? `Modifier — ${matiere.code}` : 'Nouvelle matière première'}
      subtitle={matiere ? matiere.nom : 'Saisie du catalogue MP'}
      size="md"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Code *">
            <Input value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="MP-CIM-CPJ35" required />
          </Field>
          <Field label="Unité *">
            <select
              value={form.unite}
              onChange={(e) => set('unite', e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
            >
              <option>kg</option><option>L</option><option>U</option><option>ML</option><option>M2</option><option>M3</option><option>T</option>
            </select>
          </Field>
        </div>

        <Field label="Nom *">
          <Input value={form.nom} onChange={(e) => set('nom', e.target.value)} placeholder="Ciment CPJ 35" required />
        </Field>

        <Field label="Description">
          <textarea
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
            className="flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-primary)]"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Prix unitaire HT (FCFA / unité) *">
            <Input
              type="number" min="0" step="0.01"
              value={form.prixUnitaireFcfa}
              onChange={(e) => set('prixUnitaireFcfa', e.target.value)}
              required
              placeholder="125.00"
            />
          </Field>
          <Field label="Fournisseur">
            <Input value={form.fournisseurNom} onChange={(e) => set('fournisseurNom', e.target.value)} placeholder="CIMAF" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Stock actuel">
            <Input type="number" step="0.01" value={form.stockActuel} onChange={(e) => set('stockActuel', e.target.value)} />
          </Field>
          <Field label="Seuil alerte">
            <Input type="number" step="0.01" value={form.seuilAlerte} onChange={(e) => set('seuilAlerte', e.target.value)} />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.actif} onChange={(e) => set('actif', e.target.checked)} />
          Matière active
        </label>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <Button type="button" variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" loading={submitting}>{matiere ? 'Enregistrer' : 'Créer'}</Button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
